import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs/Subscription';
import { StaticVertex, WorkflowEdge, WorkflowVertex, TypeData, SubWorkflowVertex } from 'component.workflow.core';
import {
  WorkflowDesignerDispatcherAction,
  WorkflowDesignerDispatcherActionType,
  WorkflowDesignerDispatcherService
} from '../workflow-designer-dispatcher.service';
import { AbstractWorkflowDesignerDispatcherObserver } from '../abstract-workflow-designer-dispatcher-observer';
import { WorkflowDesignComponent } from '../../../app/workflow/workflow-design.component';
import { WorkflowService } from '../../../app/services';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wf-designer-vertex-properties',
  template: `
    <h3>Properties</h3>
    <div *ngIf="vertex; else propertiesMsg">
      <p>
        Following properties are available for the selected vertex.
      </p>

      <ui-type-interface-form *ngIf="typeData" [typeData]="typeData" (formInitialized)="formInitialized($event)"
                              #interfaceForm></ui-type-interface-form>

      <section *ngIf="!vertex.incoming.empty">
        <h3>Incoming Edges</h3>
        <wf-designer-vertex-connection *ngFor="let edge of incomingEdges" [direction]="'in'"
                                       (removed)="onEdgeRemoved($event)"
                                       (moved)="onEdgeMoved($event)"
                                       [edge]="edge"></wf-designer-vertex-connection>
      </section>

      <section *ngIf="!vertex.outgoing.empty">
        <h3>Outgoing Edges</h3>
        <wf-designer-vertex-connection *ngFor="let edge of outgoingEdges" [direction]="'out'"
                                       (removed)="onEdgeRemoved($event)"
                                       (moved)="onEdgeMoved($event)"
                                       [edge]="edge"></wf-designer-vertex-connection>
      </section>

      <section class="buttons">
        <button class="btn btn-outline-danger btn-sm" (click)="onVertexRemove()">Remove</button>
      </section>

    </div>

    <ng-template #propertiesMsg>
      <p class="text-center">
        Once an item is selected on the canvas, based on its type - the properties will be displayed here.
      </p>
    </ng-template>

    <ui-overlay [visible]="!!overlayMessage">
      <h3>{{ overlayMessage }}</h3>
    </ui-overlay>
  `,
  styles: [ `

    section {
      margin-top: 1rem;
    }

    .buttons {
      text-align: right;
    }

  ` ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowDesignerVertexPropertiesComponent extends AbstractWorkflowDesignerDispatcherObserver
  implements OnChanges, OnDestroy, OnInit {
  @Input() vertex: WorkflowVertex;

  typeData: TypeData;
  formChanges$: Subscription;
  overlayMessage: string = null;

  constructor(dispatcherService: WorkflowDesignerDispatcherService,
              protected workflowService: WorkflowService,
              protected design: WorkflowDesignComponent,
              protected cdr: ChangeDetectorRef) {
    super(dispatcherService);
  }

  updateTypeData() {
    const vertex = this.vertex;

    if (vertex) {
      const typeInterface = vertex.propertyDefinitions();

      this.typeData = new TypeData({
        typeInterface: typeInterface,
        value: typeInterface.defaultObject(vertex.properties.toJSON())
      });
    } else {
      this.typeData = null;
    }

    this.repaint();
  }

  get outgoingEdges(): WorkflowEdge[] {
    return this.vertex.outgoing.ordered as WorkflowEdge[];
  }

  get incomingEdges(): WorkflowEdge[] {
    return this.vertex.incoming.ordered as WorkflowEdge[];
  }

  ngOnChanges() {
    this.updateTypeData();

    if (this.vertex) {
      this.vertex.incoming.reorder();
      this.vertex.outgoing.reorder();
    }
  }

  formInitialized(form: FormGroup) {
    this.wireForm(form);
  }

  wireForm(form: FormGroup) {

    if (this.formChanges$) {
      this.formChanges$.unsubscribe();
    }

    this.formChanges$ = form.valueChanges.subscribe(value => {
      if (!form.pristine) {

        let rebuild = false;

        if (this.vertex instanceof StaticVertex) {
          if (this.vertex.dataType !== value.dataType) {
            // Value changed so lets rebuild the complete form
            rebuild = true;
          }
        }

        const apply = (v) => {
          this.vertex.apply(v);
          this.dispatcherService.dispatchAction(WorkflowDesignerDispatcherActionType.VertexPropertiesUpdated, this, this.vertex);

          if (rebuild) {
            this.updateTypeData();
          }
        };

        if (this.vertex instanceof SubWorkflowVertex) {
          const subWorkflowId = value.subWorkflowId;

          if (this.design.provider.resolve(subWorkflowId) == null) {
            // Its not loaded. Lets load it
            this.overlayMessage = 'Loading Workflow...';
            this.workflowService.fetchByIds([subWorkflowId])
              .pipe(finalize(() => {
                this.overlayMessage = null;
                this.cdr.markForCheck();
              }))
              .subscribe(internalWorkflowList => {
              const first = internalWorkflowList.length > 0 ? internalWorkflowList[0] : null;

              if (first) {
                this.design.provider.register(first);
              }

              apply(value);
            });
          } else {
            apply(value);
          }

        } else {
          apply(value);
        }

      }
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    if (this.formChanges$) {
      this.formChanges$.unsubscribe();
      this.formChanges$ = null;
    }
  }

  repaint() {
    this.cdr.markForCheck();
  }

  onEdgeRemoved(edge: WorkflowEdge) {
    this.dispatcherService.dispatchAction(WorkflowDesignerDispatcherActionType.EdgeRemoveRequest, this, edge);
  }

  onVertexRemove() {
    this.dispatcherService.dispatchAction(WorkflowDesignerDispatcherActionType.VertexRemoveRequest, this, this.vertex);
  }

  onEdgeMoved(data) {
    this.dispatcherService.dispatchAction(WorkflowDesignerDispatcherActionType.EdgeMoveRequest, this, data);
  }

  onDispatcherEvent(action: WorkflowDesignerDispatcherAction) {
    switch (action.type) {
      case WorkflowDesignerDispatcherActionType.EdgeAdded:
        this.repaint();
        break;

      case WorkflowDesignerDispatcherActionType.VertexPropertiesUpdated:

        if (action.data === this.vertex && action.originator !== this) {
          this.updateTypeData();
        }

        break;
    }
  }
}



// WEBPACK FOOTER //
// ./src/library/designer/vertex/workflow-designer-vertex-properties.component.ts